// ============================================
// COMPONENT - Modal abrir caja para un vendedor (propietario)
// ============================================

import { useState } from 'react';
import { Button } from '@almacenes/ui';
import { useVendedoresCaja, type AbrirTurnoDto } from '@almacenes/core';
import { MdClose } from 'react-icons/md';
import { rangoAperturaRetroactiva, formatMontoInput } from '../utils/formato';

interface AbrirTurnoParaVendedorModalProps {
  onClose: () => void;
  onConfirmar: (dto: AbrirTurnoDto) => void;
  isLoading: boolean;
}

export const AbrirTurnoParaVendedorModal = ({ onClose, onConfirmar, isLoading }: AbrirTurnoParaVendedorModalProps) => {
  const { data: vendedores, isLoading: isLoadingVendedores } = useVendedoresCaja();
  const [usuarioId, setUsuarioId] = useState<string>('');
  const [montoDisplay, setMontoDisplay] = useState('');
  const [montoRaw, setMontoRaw] = useState('');
  const [observacion, setObservacion] = useState('');
  const [mostrarHoraPersonalizada, setMostrarHoraPersonalizada] = useState(false);
  const [fechaHoraApertura, setFechaHoraApertura] = useState('');
  const [errors, setErrors] = useState<{ usuarioId?: string; monto?: string; fechaHora?: string }>({});

  const { min: minFechaHora, max: maxFechaHora } = rangoAperturaRetroactiva();

  const handleMontoChange = (value: string) => {
    const soloNumeros = value.replace(/\D/g, '');
    setMontoRaw(soloNumeros);
    setMontoDisplay(formatMontoInput(soloNumeros));
    if (errors.monto) setErrors((prev) => ({ ...prev, monto: undefined }));
  };

  const handleConfirmar = () => {
    const newErrors: typeof errors = {};
    if (!usuarioId) newErrors.usuarioId = 'Elige el vendedor';
    if (!montoRaw || Number(montoRaw) < 0) newErrors.monto = 'El monto inicial es obligatorio';
    if (mostrarHoraPersonalizada && !fechaHoraApertura) newErrors.fechaHora = 'Indica la hora real de apertura';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onConfirmar({
      usuarioId: Number(usuarioId),
      montoInicial: Number(montoRaw),
      observacion: observacion.trim() || undefined,
      fechaHoraApertura: mostrarHoraPersonalizada ? fechaHoraApertura : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl font-bold" style={{ color: '#11194F' }}>Abrir caja para un vendedor</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <MdClose className="text-2xl" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Vendedor <span className="text-red-500">*</span>
            </label>
            <select
              value={usuarioId}
              onChange={(e) => { setUsuarioId(e.target.value); if (errors.usuarioId) setErrors((prev) => ({ ...prev, usuarioId: undefined })); }}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                errors.usuarioId ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
              }`}
              disabled={isLoading || isLoadingVendedores}
            >
              <option value="">Selecciona un vendedor</option>
              {vendedores?.map((v) => (
                <option key={v.id} value={v.id}>{v.nombreMostrable}</option>
              ))}
            </select>
            {errors.usuarioId && <p className="text-red-500 text-sm mt-1">{errors.usuarioId}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Monto inicial (CLP) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={montoDisplay}
                onChange={(e) => handleMontoChange(e.target.value)}
                placeholder="15.000"
                className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                  errors.monto ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                }`}
                disabled={isLoading}
              />
            </div>
            {errors.monto && <p className="text-red-500 text-sm mt-1">{errors.monto}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
              Observación (opcional)
            </label>
            <input
              type="text"
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
              disabled={isLoading}
            />
          </div>

          <div>
            <button
              type="button"
              onClick={() => {
                setMostrarHoraPersonalizada((v) => !v);
                setErrors((prev) => ({ ...prev, fechaHora: undefined }));
              }}
              className="text-sm font-medium underline"
              style={{ color: '#43BFFF' }}
              disabled={isLoading}
            >
              {mostrarHoraPersonalizada ? 'Usar la hora actual' : '¿El vendedor olvidó abrir su caja antes? Elige la hora real'}
            </button>
            {mostrarHoraPersonalizada && (
              <div className="mt-3">
                <label className="block text-sm font-medium mb-2" style={{ color: '#11194F' }}>
                  Hora real de apertura <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={fechaHoraApertura}
                  min={minFechaHora}
                  max={maxFechaHora}
                  onChange={(e) => {
                    setFechaHoraApertura(e.target.value);
                    if (errors.fechaHora) setErrors((prev) => ({ ...prev, fechaHora: undefined }));
                  }}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.fechaHora ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-blue-200'
                  }`}
                  disabled={isLoading}
                />
                {errors.fechaHora && <p className="text-red-500 text-sm mt-1">{errors.fechaHora}</p>}
                <p className="text-gray-500 text-xs mt-1">
                  Las ventas que el vendedor hizo desde esa hora se vincularán automáticamente a este turno.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 pt-6">
          <Button variant="secondary" onClick={onClose} disabled={isLoading} className="flex-1">
            Cancelar
          </Button>
          <Button variant="success" onClick={handleConfirmar} isLoading={isLoading} disabled={isLoading} className="flex-1">
            Abrir caja
          </Button>
        </div>
      </div>
    </div>
  );
};
